import React, { createContext, useContext, useState, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { TrackData, TrackCreationContextType } from "./types";
import { getTemporaryTrack, excelToJson } from "../api/track";
import { getWaypointsForTrack } from "../api/waypoint";
import { GetTemporaryTrackResponse } from "../types/requestsOutputs";
import { useSaveTrack } from "../hooks/useSaveTrack";

const BIKE_FILTER_ID = "cmbikefilter00013ic0ytonbe7q";
export const IMAGE_SCAN_FILTER_ID = "cmk7xgxhw09wbni2mdnwwn0uc";
export const CAR_CATEGORY_ID = "iduniquecar";
const FREE_WALKING_TOUR_FILTER_ID = "cmcj0hywj00013ic0ytonbe7q";

const TrackCreationContext = createContext<TrackCreationContextType | undefined>(undefined);

export const useTrackCreation = (): TrackCreationContextType => {
    const context = useContext(TrackCreationContext);
    if (!context) {
        throw new Error("useTrackCreation must be used within a TrackCreationProvider");
    }
    return context;
};

export const TrackCreationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [trackData, setTrackData] = useState<TrackData>({
        id: uuidv4(),
        language: "",
        type: "",
    });
    const [selectedStoryObjectId, setSelectedStoryObjectId] = useState<string>("all");
    const token = localStorage.getItem("token") || "";

    const getTrack = useCallback(
        async (id: string): Promise<GetTemporaryTrackResponse | null> => {
            try {
                const response = await getTemporaryTrack({ token, id });
                if (!response) {
                    console.error("Error retrieving track:", response);
                    return null;
                }
                return response;
            } catch (error) {
                console.error("Error retrieving track:", error);
                return null;
            }
        },
        [token],
    );

    const updateTrackData = useCallback((field: keyof TrackData, value: any) => {
        setTrackData((prev) => ({ ...prev, [field]: value }));
    }, []);

    const createTrack = useCallback((id: string): TrackData => {
        const newTrack: TrackData = { id, language: "", type: "" };
        setTrackData(newTrack);
        return newTrack;
    }, []);

    const loadTrackData = useCallback(
        async (id: string) => {
            const track = await getTrack(id);
            if (!track) {
                createTrack(id);
                return;
            }

            let trackMock: TrackData;

            if (track.isTemporary) {
                const tempTrack = track.temporaryTrack;
                if (!tempTrack) {
                    createTrack(id);
                    return;
                }

                trackMock = {
                    id: tempTrack.inernalId,
                    city: tempTrack.cityId,
                    trackId: tempTrack.trackId,
                    description: tempTrack.description,
                    duration: tempTrack.duration,
                    guide: tempTrack.guideId,
                    highlights: tempTrack.highlights,
                    language: tempTrack.language,
                    length: tempTrack.length,
                    remoteAccess: tempTrack.remoteAccess,
                    unlockModeFree: tempTrack.unlockModeFree,
                    subtitle: tempTrack.subtitle,
                    title: tempTrack.title,
                    type: tempTrack.type,
                    subType: tempTrack.subType,
                    price: tempTrack.price,
                    promotion: tempTrack.promotion,
                    photos: tempTrack.photos,
                    country: tempTrack.countryId,
                    position: tempTrack.position,
                    generationOption: tempTrack.aigeneration,
                    excelPath: tempTrack.excelPath,
                    numberOfWords: tempTrack.numberOfWords,
                    theme: tempTrack.theme,
                    audience: tempTrack.audience,
                    languages: tempTrack.languages,
                    isDialog: tempTrack.isDialog,
                    isGenerateTextOnly: tempTrack.isGenerateTextOnly,
                    isGenerateTextAndAudio: tempTrack.isGenerateTextAndAudio,
                    voice: tempTrack.voice,
                    voiceSecond: tempTrack.voiceSecond,
                    museumName: tempTrack.museumName,
                    isMuseumList: tempTrack.isMuseumList,
                    museumPictureUrl: tempTrack.museumPictureUrl,
                    museumInfo: tempTrack.museumInfo,
                    forceDownload: (tempTrack.forceDownload === true
                        ? "force"
                        : tempTrack.forceDownload === false
                          ? "recommend"
                          : "none") as unknown as TrackData["forceDownload"],
                    museumSearchQrcode: tempTrack.museumSearchQrcode,
                    hasStops: tempTrack.hasStops,
                    startingLat: tempTrack.startingLat,
                    startingLng: tempTrack.startingLng,
                    startingAddress: tempTrack.startingAddress,
                    ratingLink: tempTrack.ratingLink,
                    numberOfListeningsBeforeRating: tempTrack.numberOfListeningsBeforeRating,
                    isPaid: tempTrack.price && tempTrack.price > 0 ? true : false,
                    isStartAnywhere: tempTrack.isStartAnywhere ? tempTrack.isStartAnywhere : false,
                    isWithoutRoutes: tempTrack.isWithoutRoutes ? tempTrack.isWithoutRoutes : false,
                    isWithoutMap: tempTrack.isWithoutMap ? tempTrack.isWithoutMap : false,
                    hasProgressionSystem: tempTrack.hasProgressionSystem ? tempTrack.hasProgressionSystem : false,
                    hasSocialSharePopup: tempTrack.hasSocialSharePopup ? tempTrack.hasSocialSharePopup : false,
                    socialShareImage: tempTrack.socialShareImage,
                    displayWaypointIndexNumbers: tempTrack.displayWaypointIndexNumbers
                        ? tempTrack.displayWaypointIndexNumbers
                        : false,
                    hideTrackRatings: tempTrack.hideTrackRatings ? tempTrack.hideTrackRatings : false,
                    defaultTriggerMode: tempTrack.defaultTriggerMode as "GPS" | "GPSAlert" | "IMAGE_SCAN" | undefined,
                };
            } else {
                const finalTrack = track.track;
                const temporaryTrack = track.temporaryTrack;

                if (!finalTrack) {
                    createTrack(id);
                    return;
                }

                trackMock = {
                    id: finalTrack.id,
                    city: finalTrack.cityId,
                    trackId: finalTrack.id,
                    guide: finalTrack.tguideId,
                    length: finalTrack.length,
                    remoteAccess: finalTrack.remoteAccess ? "remote" : "onsite",
                    unlockModeFree: temporaryTrack?.unlockModeFree ?? false,
                    price: finalTrack.price,
                    promotion: finalTrack.promotionPercentage,
                    language: finalTrack.creationLanguage,
                    duration: finalTrack.duration,
                    description: finalTrack.trackLocalizeds[0]?.description ?? temporaryTrack?.description,
                    highlights: finalTrack.trackLocalizeds[0]?.highlights ?? temporaryTrack?.highlights,
                    subtitle: finalTrack.trackLocalizeds[0]?.subtitle ?? temporaryTrack?.subtitle,
                    title: finalTrack.trackLocalizeds[0]?.name ?? temporaryTrack?.title,
                    durationText: finalTrack.trackLocalizeds[0]?.durationText ?? null,
                    departureText: finalTrack.trackLocalizeds[0]?.departureText ?? null,
                    availableLanguages: finalTrack.availableLanguagesTab,
                    position: finalTrack.position,
                    subType: finalTrack.filterId,
                    generationOption: temporaryTrack?.aigeneration,
                    excelPath: temporaryTrack?.excelPath,
                    numberOfWords: temporaryTrack?.numberOfWords,
                    theme: temporaryTrack?.theme,
                    audience: temporaryTrack?.audience,
                    languages: temporaryTrack?.languages,
                    isDialog: temporaryTrack?.isDialog,
                    isGenerateTextOnly: temporaryTrack?.isGenerateTextOnly,
                    isGenerateTextAndAudio: temporaryTrack?.isGenerateTextAndAudio,
                    voice: temporaryTrack?.voice,
                    voiceSecond: temporaryTrack?.voiceSecond,
                    waypoints: finalTrack.waypoints,
                    museumName: finalTrack?.masterPlan?.internalName ?? temporaryTrack?.museumName,
                    isMuseumList:
                        finalTrack?.masterPlan?.showPlan != null
                            ? !finalTrack.masterPlan?.showPlan
                            : temporaryTrack?.isMuseumList,
                    museumPictureUrl: finalTrack?.masterPlan?.pictureUrl ?? temporaryTrack?.museumPictureUrl,
                    museumInfo:
                        finalTrack?.masterPlan?.masterPlanLocalized?.[0]?.museumInfo ?? temporaryTrack?.museumInfo,
                    forceDownload: (finalTrack?.downloadForced
                        ? "force"
                        : finalTrack?.downloadOptional
                          ? "recommend"
                          : "none") as unknown as TrackData["forceDownload"],
                    museumSearchQrcode: finalTrack?.masterPlan?.hasQRCode,
                    hasStops: temporaryTrack?.hasStops,
                    startingLat: finalTrack?.startingCoordinates?.lat ?? temporaryTrack?.startingLat,
                    startingLng: finalTrack?.startingCoordinates?.lng ?? temporaryTrack?.startingLng,
                    startingAddress: finalTrack?.startingPoint ?? temporaryTrack?.startingAddress,
                    ratingLink: finalTrack?.ratingLink,
                    numberOfListeningsBeforeRating: finalTrack?.numberOfListeningsBeforeRating,
                    isPaid: finalTrack?.price && finalTrack.price > 0 ? true : false,
                    isStartAnywhere: finalTrack.isStartAnywhere ? finalTrack.isStartAnywhere : false,
                    isWithoutRoutes: finalTrack.isWithoutRoutes ? finalTrack.isWithoutRoutes : false,
                    isWithoutMap: finalTrack.isWithoutMap ? finalTrack.isWithoutMap : false,
                    hasProgressionSystem: finalTrack.hasProgressionSystem ? finalTrack.hasProgressionSystem : false,
                    hasSocialSharePopup: finalTrack.hasSocialSharePopup ? finalTrack.hasSocialSharePopup : false,
                    socialShareImage: finalTrack.socialShareImage,
                    displayWaypointIndexNumbers: finalTrack.displayWaypointIndexNumbers
                        ? finalTrack.displayWaypointIndexNumbers
                        : false,
                    hideTrackRatings: finalTrack.hideTrackRatings ? finalTrack.hideTrackRatings : false,
                    filterId: finalTrack?.filterId,
                    defaultTriggerMode: (finalTrack.defaultTriggerMode ?? temporaryTrack?.defaultTriggerMode) as
                        | "GPS"
                        | "GPSAlert"
                        | "IMAGE_SCAN"
                        | undefined,
                };

                // Determine track type from flags and filter
                // Note: Free walking tour has isHOHO=true but should be treated as walk category
                if (finalTrack.filterId === FREE_WALKING_TOUR_FILTER_ID) {
                    trackMock.type = "iduniquewalk";
                } else if (finalTrack.isHOHO) {
                    trackMock.type = "iduniquebus";
                } else if (finalTrack.isIsometricInterior) {
                    trackMock.type = "iduniquemusee";
                } else if (finalTrack.filterId === BIKE_FILTER_ID) {
                    trackMock.type = "iduniquebike";
                } else if (finalTrack.filterId === IMAGE_SCAN_FILTER_ID) {
                    trackMock.type = CAR_CATEGORY_ID;
                } else {
                    trackMock.type = "iduniquewalk";
                }

                if (finalTrack.pictures && finalTrack.pictures.length > 0) {
                    trackMock.photos = finalTrack.pictures.map((picture) => picture.url);
                }

                if (finalTrack.city) {
                    trackMock.country = finalTrack.city.countryId;
                }

                trackMock.remoteAccess = finalTrack.remoteAccess ? "remote" : "onsite";
            }

            setTrackData(trackMock);
        },
        [getTrack, createTrack],
    );

    const clearTrackData = useCallback(() => {
        const newTrack: TrackData = { id: uuidv4(), language: "", type: "" };
        setTrackData(newTrack);
        setSelectedStoryObjectId("all"); // Reset story filter when clearing track
        localStorage.removeItem("tracks");
    }, []);

    const saveTrack = useSaveTrack(trackData, token, updateTrackData);

    const generateWaypointsFromExcel = useCallback(
        async (path: string, trackId: string) => {
            try {
                await excelToJson({ token, path, trackId });
            } catch (error) {
                console.error("Error generating waypoints from Excel:", error);
            }
        },
        [token],
    );

    const loadWaypointData = useCallback(
        async (trackId: string) => {
            if (trackId) {
                const waypoints = await getWaypointsForTrack({ token, trackId });
                if (waypoints) {
                    setTrackData((prev) => ({ ...prev, waypoints: waypoints.waypoints }));
                }
            }
        },
        [token],
    );

    return (
        <TrackCreationContext.Provider
            value={{
                trackData,
                updateTrackData,
                clearTrackData,
                createTrack,
                loadTrackData,
                saveTrack,
                getTrack,
                generateWaypointsFromExcel,
                loadWaypointData,
                selectedStoryObjectId,
                setSelectedStoryObjectId,
            }}
        >
            {children}
        </TrackCreationContext.Provider>
    );
};
